import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { hostelService } from '../services/api';
import { Hostel } from '../types';
import './HostelProfile.css';
import './Dashboard.css';

const HostelProfileEdit: React.FC = () => {
  const { hostel: ctxHostel, selectHostel, isHostelAdmin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<Partial<Hostel>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const loadHostel = useCallback(async () => {
    if (!ctxHostel?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await hostelService.getHostelById(ctxHostel.id);
      setForm({ ...data });
    } catch {
      toast.error('Failed to load hostel');
      navigate('/profile');
    } finally {
      setLoading(false);
    }
  }, [ctxHostel?.id, navigate]);

  useEffect(() => {
    if (!isHostelAdmin()) {
      navigate('/profile', { replace: true });
      return;
    }
    void loadHostel();
  }, [isHostelAdmin, loadHostel, navigate]);

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !ctxHostel?.id) return;
    setLogoUploading(true);
    try {
      const updated = await hostelService.uploadHostelLogo(ctxHostel.id, file);
      toast.success('Logo uploaded');
      setForm((prev) => ({ ...prev, logo: updated.logo }));
      if (ctxHostel.id === updated.id) selectHostel(updated);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Logo upload failed');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ctxHostel?.id) return;
    if (!form.name?.trim() || !form.address?.trim()) {
      toast.error('Name and address are required');
      return;
    }
    setSaving(true);
    try {
      const updated = await hostelService.updateHostel(ctxHostel.id, {
        name: form.name.trim(),
        description: form.description?.trim() || '',
        address: form.address.trim(),
        city: form.city?.trim() || '',
        state: form.state?.trim() || '',
        pincode: form.pincode?.trim() || '',
        country: form.country?.trim() || '',
        phone: form.phone?.trim() || '',
        email: form.email?.trim() || '',
        websiteUrl: form.websiteUrl?.trim() || '',
        gstNumber: form.gstNumber?.trim() || '',
        registrationNumber: form.registrationNumber?.trim() || '',
        bankAccountHolder: form.bankAccountHolder?.trim() || '',
        bankAccountNumber: form.bankAccountNumber?.trim() || '',
        bankIfscCode: form.bankIfscCode?.trim() || '',
        bankName: form.bankName?.trim() || '',
        status: form.status,
      });
      selectHostel(updated);
      toast.success('Hostel details updated');
      navigate('/profile');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading…</div>;
  }

  return (
    <div className="hostel-profile-page">
      <button type="button" className="hostel-profile-back" onClick={() => navigate('/profile')}>
        ← Back to profile
      </button>
      <header className="hostel-profile-header">
        <h1>Edit hostel details</h1>
      </header>

      <form className="hostel-profile-card hostel-profile-form" onSubmit={handleSubmit}>
        {form.hostelCode ? (
          <div className="form-group">
            <label>Hostel code</label>
            <input type="text" readOnly value={form.hostelCode} className="hostel-code-readonly" />
            <small className="text-muted">Cannot be changed after creation.</small>
          </div>
        ) : null}

        <div className="form-group hostel-edit-logo">
          <label>Logo</label>
          <div className="hostel-edit-logo-row">
            <div className="hostel-edit-logo-preview">
              {form.logo ? (
                <img src={form.logo} alt="" className="hostel-edit-logo-img" />
              ) : (
                <span className="hostel-table-logo-fallback" aria-hidden>
                  🏢
                </span>
              )}
            </div>
            <div className="hostel-edit-logo-actions">
              <input
                type="file"
                id="profile-hostel-logo"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hostel-logo-file-input"
                disabled={logoUploading || saving}
                onChange={handleLogoFile}
              />
              <label htmlFor="profile-hostel-logo" className="btn btn-sm btn-primary hostel-logo-upload-label">
                {logoUploading ? 'Uploading…' : 'Upload logo'}
              </label>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            required
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            value={form.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Address *</label>
          <input
            type="text"
            required
            value={form.address || ''}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </div>

        <div className="hostel-profile-form-row">
          <div className="form-group">
            <label>City</label>
            <input type="text" value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="form-group">
            <label>State</label>
            <input type="text" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          </div>
        </div>

        <div className="hostel-profile-form-row">
          <div className="form-group">
            <label>Pincode</label>
            <input
              type="text"
              value={form.pincode || ''}
              onChange={(e) => setForm({ ...form, pincode: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input
              type="text"
              value={form.country || ''}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
            />
          </div>
        </div>

        <div className="hostel-profile-form-row">
          <div className="form-group">
            <label>Phone</label>
            <input type="tel" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={form.email || ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Website</label>
          <input
            type="url"
            value={form.websiteUrl || ''}
            onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
            placeholder="https://"
          />
        </div>

        <div className="hostel-profile-form-row">
          <div className="form-group">
            <label>GST number</label>
            <input
              type="text"
              value={form.gstNumber || ''}
              onChange={(e) => setForm({ ...form, gstNumber: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Registration number</label>
            <input
              type="text"
              value={form.registrationNumber || ''}
              onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
            />
          </div>
        </div>

        <div className="hostel-profile-form-row">
          <div className="form-group">
            <label>Account holder</label>
            <input
              type="text"
              value={form.bankAccountHolder || ''}
              onChange={(e) => setForm({ ...form, bankAccountHolder: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Bank name</label>
            <input
              type="text"
              value={form.bankName || ''}
              onChange={(e) => setForm({ ...form, bankName: e.target.value })}
            />
          </div>
        </div>

        <div className="hostel-profile-form-row">
          <div className="form-group">
            <label>Account number</label>
            <input
              type="text"
              value={form.bankAccountNumber || ''}
              onChange={(e) => setForm({ ...form, bankAccountNumber: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>IFSC code</label>
            <input
              type="text"
              value={form.bankIfscCode || ''}
              onChange={(e) => setForm({ ...form, bankIfscCode: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            value={form.status || 'ACTIVE'}
            onChange={(e) => setForm({ ...form, status: e.target.value as Hostel['status'] })}
          >
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
            <option value="SUSPENDED">SUSPENDED</option>
          </select>
        </div>

        <div className="hostel-profile-form-actions">
          <button type="button" className="hostel-profile-btn" disabled={saving} onClick={() => navigate('/profile')}>
            Cancel
          </button>
          <button type="submit" className="hostel-profile-btn hostel-profile-btn--primary" disabled={saving || logoUploading}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HostelProfileEdit;
